import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { useNotifications as useNativeNotifications } from '../hooks/useNotifications';
import { useAppSounds } from '../hooks/useAppSounds';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import type { AppNotification } from '../services/modules/notifications/notification.types';
import {
    loadNotifications,
    saveNotifications,
    withWelcomeNotification,
} from '../services/modules/notifications/notification.storage';
import { filterVisibleNotifications } from '../services/modules/notifications/notification.visibility';

export type { AppNotification } from '../services/modules/notifications/notification.types';

interface NotificationsContextType {
    visibleNotifications: AppNotification[];
    unreadCount: number;
    addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    removeNotification: (id: string) => void;
    clearAllNotifications: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const { currentOrganization, organizations } = useOrganization();
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const hydratedUserIdRef = useRef<string | null>(null);
    const orgIdsKey = organizations.map((o) => o.id).join(',');

    const { sendNotification: sendNativeNotification } = useNativeNotifications();
    const { playSound } = useAppSounds();

    useEffect(() => {
        if (!user?.id) {
            hydratedUserIdRef.current = null;
            setNotifications([]);
            return;
        }

        if (hydratedUserIdRef.current === user.id) return;
        hydratedUserIdRef.current = user.id;

        setNotifications(loadNotifications(user.id, organizations.map((o) => o.id)));
    }, [user?.id, orgIdsKey, organizations]);

    useEffect(() => {
        if (!user?.id) return;

        setNotifications((prev) => withWelcomeNotification(prev, user.id, currentOrganization));
    }, [user?.id, orgIdsKey, currentOrganization?.id, currentOrganization?.name]);

    useEffect(() => {
        if (!user?.id) return;
        saveNotifications(user.id, notifications);
    }, [notifications, user?.id]);

    const visibleNotifications = useMemo(() => {
        if (!user?.id) return [];
        return filterVisibleNotifications(notifications, user.id, organizations)
            .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    }, [notifications, user?.id, organizations]);

    const visibleIds = useMemo(
        () => new Set(visibleNotifications.map((n) => n.id)),
        [visibleNotifications]
    );

    const addNotification = useCallback((data: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
        setNotifications((prev) => {
            if (data.tag && prev.some((n) => n.tag === data.tag)) {
                return prev;
            }

            const newNotification: AppNotification = {
                id: crypto.randomUUID(),
                timestamp: new Date(),
                read: false,
                ...data,
                organizationId: data.organizationId ?? currentOrganization?.id,
                organizationName: data.organizationName ?? currentOrganization?.name,
                recipientUserId: data.recipientUserId ?? user?.id,
                recipientUserName:
                    data.recipientUserName ??
                    (user?.user_metadata?.full_name as string | undefined) ??
                    user?.email,
            };

            setTimeout(() => {
                if (data.type === 'task') {
                    playSound('alert');
                    sendNativeNotification(data.title, { body: data.message, tag: data.tag || 'task-alert' });
                } else if (data.type === 'success' || data.type === 'info') {
                    playSound('message');
                }
            }, 0);

            return [newNotification, ...prev];
        });
    }, [playSound, sendNativeNotification, currentOrganization?.id, currentOrganization?.name, user]);

    const markAsRead = useCallback((id: string) => {
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications((prev) =>
            prev.map((n) => (visibleIds.has(n.id) ? { ...n, read: true } : n))
        );
    }, [visibleIds]);

    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications((prev) => prev.filter((n) => !visibleIds.has(n.id)));
    }, [visibleIds]);

    const unreadCount = visibleNotifications.filter((n) => !n.read).length;

    return (
        <NotificationsContext.Provider value={{
            visibleNotifications,
            unreadCount,
            addNotification,
            markAsRead,
            markAllAsRead,
            removeNotification,
            clearAllNotifications,
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotificationsContext = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotificationsContext must be used within a NotificationsProvider');
    }
    return context;
};
